import { Component, Input, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GetResourceCountriesUseCase } from '@/core/domain/resource-countries/usecases/getResourceCountries.usecase';
import { Country } from '@/core/domain/resource-countries/models/resource-countries-data.model';
import { CdnService } from '@/common/services/cdn/cdn.service';

const DEFAULT_LADA = '+52';

@Component({
  selector: 'fe-phone-lada-input',
  templateUrl: './phone-lada-input.component.html',
  styleUrls: ['./phone-lada-input.component.scss']
})
export class PhoneLadaInputComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() label = '';
  @Input() ladaControlName = 'lada';
  @Input() phoneControlName = 'phone';
  @Input() autocomplete: string | null = null;

  @ViewChild('ladaSelector') ladaSelector!: ElementRef;

  countries: Country[] = [];
  selectedCountry?: Country;
  dropdownOpen = false;
  search = '';

  readonly errorIcon = `url(${CdnService.url('images/mini-error.svg')})`;

  private readonly PRIORITY_ISO2 = ['MX', 'US'];

  constructor(private readonly getResourceCountriesUseCase: GetResourceCountriesUseCase) {}

  private get ladaControl() {
    return this.form?.get(this.ladaControlName);
  }

  ngOnInit(): void {
    this.getResourceCountriesUseCase.execute().subscribe((response) => {
      const all = response.data?.countries ?? [];
      const top = this.PRIORITY_ISO2
        .map((iso) => all.find((c) => c.iso2 === iso))
        .filter((c): c is Country => !!c);
      this.countries = [...top, ...all.filter((c) => !top.includes(c))];
      const current = this.ladaControl?.value || DEFAULT_LADA;
      this.selectedCountry =
        this.countries.find((c) => c.lada === current) ??
        this.countries.find((c) => c.iso2 === 'MX') ??
        this.countries[0];
      if (!this.ladaControl?.value) {
        this.ladaControl?.setValue(this.selectedCountry?.lada);
      }
    });
  }

  get currentCountry(): Country | undefined {
    const current = this.ladaControl?.value;
    return (
      this.countries.find((c) => c.lada === current) ?? this.selectedCountry
    );
  }

  get filteredCountries(): Country[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.countries;
    return this.countries.filter(
      (c) => c.iso2.toLowerCase().includes(term) || c.lada.includes(term),
    );
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    this.search = '';
  }

  selectCountry(country: Country): void {
    this.selectedCountry = country;
    this.ladaControl?.setValue(country.lada);
    this.dropdownOpen = false;
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (this.dropdownOpen && this.ladaSelector && !this.ladaSelector.nativeElement.contains(target as Node)) {
      this.dropdownOpen = false;
    }
  }
}
